import React, { useEffect, useRef, useState } from "react";
import { Select, useGLTF } from "@react-three/drei";
import { moveObject, rotateObject } from "../../../../animations/objects";
import { useDrawer, useOutline, useSection } from "../../../../common/hooks";
import Book from "./Book";
import { books, rowPositions } from "../../../../constants/books";
import { SECTIONS } from "../../../../constants/sections";

const model = "/models/library/library.glb";

export default function Library({ ...props }) {
  const group = useRef();
  const { nodes, materials } = useGLTF(model);

  const { addOutline, clearOutline } = useOutline();
  const { isOpen } = useDrawer();
  const { currentSection } = useSection();

  const [selectedBook, setBook] = useState(null);
  const [prevPos, setPrevPos] = useState({ positionX: null, rotationZ: null });

  const movAmount = 0.25;
  const rotAmount = 0.4;

  const onPointerEnter = ({ object }) => {
    if (currentSection.name === SECTIONS.LIBRARY) addOutline(object);
  };

  const onPointerLeave = ({ object }) => {
    if (currentSection.name === SECTIONS.LIBRARY && selectedBook !== object)
      clearOutline();
  };

  const selectBook = (bookObj) => {
    setPrevPos({
      positionX: bookObj.position.x,
      rotationZ: bookObj.rotation.z,
    });
    moveObject(
      bookObj,
      { x: bookObj.position.x + movAmount },
      { duration: 500 }
    );
    rotateObject(
      bookObj,
      { z: bookObj.rotation.z - rotAmount },
      { duration: 500, delay: 50 }
    );
    setBook(bookObj);
  };

  const unselectBook = (bookObj) => {
    moveObject(bookObj, { x: prevPos.positionX - 0.001 }, { duration: 500 });
    rotateObject(
      bookObj,
      { z: prevPos.rotationZ + 0.0001 },
      { duration: 500, delay: 50 }
    );
    setBook(null);
  };

  useEffect(() => {
    if (!isOpen && selectedBook) unselectBook(selectedBook);
  }, [isOpen]);

  return (
    <group position={[0, 0, -0.006]} ref={group} {...props} dispose={null}>
      <group position={[-2.84, 1.26, -2.37]}>
        <mesh
          castShadow
          geometry={nodes.Cube010.geometry}
          material={materials.white}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cube010_1.geometry}
          material={materials.wood}
        />
      </group>
      <Select onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
        <group name="top-row" position={rowPositions.top}>
          {books.topRow.map((book, index) => (
            <Book
              index={index}
              key={book.name}
              text={book.name}
              color={book.color}
              position={book.position}
              textPosition={book.textPosition}
              scale={book.scale}
              book={{ selectedBook, setBook }}
              prevPos={{ prevPos, setPrevPos }}
              selectBook={selectBook}
              unselectBook={unselectBook}
            />
          ))}
        </group>

        <group name="middle-row" position={rowPositions.middle}>
          {books.middleRow.map((book, index) => (
            <Book
              index={index + books.topRow.length}
              key={book.name}
              text={book.name}
              color={book.color}
              position={book.position}
              textPosition={book.textPosition}
              scale={book.scale}
              book={{ selectedBook, setBook }}
              prevPos={{ prevPos, setPrevPos }}
              selectBook={selectBook}
              unselectBook={unselectBook}
            />
          ))}
        </group>

        <group name="bottom-row" position={rowPositions.bottom}>
          {books.bottomRow.map((book, index) => (
            <Book
              index={index + books.topRow.length + books.middleRow.length}
              key={book.name}
              text={book.name}
              color={book.color}
              position={book.position}
              textPosition={book.textPosition}
              scale={book.scale}
              book={{ selectedBook, setBook }}
              prevPos={{ prevPos, setPrevPos }}
              selectBook={selectBook}
              unselectBook={unselectBook}
            />
          ))}
        </group>
      </Select>
    </group>
  );
}

useGLTF.preload(model);
